import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import util from 'preact-util';

const SOURCE_OPTIONS_OWN = [
    { value: 'user', label: 'My Images' },
    { value: 'raceClassContestant', label: 'Contestant Images' },
];
const SOURCE_OPTIONS_ADMIN = [
    { value: 'article', label: 'Articles' },
    { value: 'news', label: 'News' },
    { value: 'product', label: 'Products' },
    { value: 'race', label: 'Races' },
    { value: 'raceClass', label: 'Race Classes' },
];

@observer
class ImageBrowser extends Component {
    constructor(props) {
        super(props);
        this.state = {
            source: '',
            search: '',
            offset: 0,
            limit: 50,
            selectedImage: null,
            searchTimer: null,
        };
    }

    componentDidMount() {
        this.loadImages();
    }

    componentWillUnmount() {
        if (this.state.searchTimer) {
            clearTimeout(this.state.searchTimer);
        }
    }

    loadImages = (append = false) => {
        const { imageBrowserStore } = this.props.stores;
        const { source, search, offset, limit } = this.state;
        const { myImagesOnly, userId } = this.props;
        const user = myImagesOnly && userId ? userId : '';
        imageBrowserStore.loadImages({ source, search, user, limit, offset, append });
    }

    handleSourceChange = (e) => {
        this.setState({ source: e.target.value, offset: 0 }, () => {
            this.loadImages();
        });
    }

    handleSearchInput = (e) => {
        const search = e.target.value;
        if (this.state.searchTimer) {
            clearTimeout(this.state.searchTimer);
        }
        const timer = setTimeout(() => {
            this.setState({ offset: 0 }, () => {
                this.loadImages();
            });
        }, 500);
        this.setState({ search, searchTimer: timer });
    }

    handleLoadMore = () => {
        const { imageBrowserStore } = this.props.stores;
        const { images, total } = imageBrowserStore;
        if (images.length < total) {
            this.setState({ offset: images.length }, () => {
                this.loadImages(true);
            });
        }
    }

    handleImageClick = (item) => {
        const { onSelect } = this.props;
        if (onSelect) {
            onSelect(item.image);
            return;
        }
        this.setState({ selectedImage: item });
    }

    handleClosePreview = () => {
        this.setState({ selectedImage: null });
    }

    getImageSrc = (image) => {
        return image.s3MediumLink || image.s3SmallLink || image.s3ThumbLink || image.s3Link || '';
    }

    getImageThumb = (image) => {
        return image.s3SmallLink || image.s3ThumbLink || image.s3MediumLink || image.s3Link || '';
    }

    getImageFull = (image) => {
        return image.s3LargeLink || image.s3MediumLink || image.s3Link || '';
    }

    render() {
        const { imageBrowserStore, userStore, raceStore } = this.props.stores;
        const { images, loading, total } = imageBrowserStore;
        const { source, search, selectedImage } = this.state;
        const { onSelect, compact } = this.props;

        const isAdmin = userStore ? userStore.isAdmin : false;
        const isCustomerAdmin = userStore ? userStore.isCustomerAdmin : false;
        const isRaceAdmin = raceStore ? raceStore.isRaceAdmin : false;
        const hasAdminAccess = isAdmin || isRaceAdmin || isCustomerAdmin;

        const sourceOptions = [
            { value: '', label: 'All' },
            ...SOURCE_OPTIONS_OWN,
            ...(hasAdminAccess ? SOURCE_OPTIONS_ADMIN : []),
        ];

        return (
            <div class={compact ? 'p-2' : 'p-3'}>
                {/* Filter bar */}
                <div class='d-flex flex-wrap gap-2 mb-3 align-items-center'>
                    <select
                        class='form-select form-select-sm'
                        style='max-width: 160px;'
                        value={source}
                        onChange={this.handleSourceChange}
                    >
                        {sourceOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <input
                        type='text'
                        class='form-control form-control-sm'
                        style='max-width: 250px;'
                        placeholder='Search images...'
                        value={search}
                        onInput={this.handleSearchInput}
                    />
                    <small class='text-body-secondary'>
                        {total} image{total !== 1 ? 's' : ''} found
                    </small>
                </div>

                {/* Image grid */}
                <div class='d-flex flex-wrap gap-2'>
                    {images.map((item, idx) => {
                        const thumb = this.getImageThumb(item.image);
                        if (!thumb) {
                            return null;
                        }
                        return (
                            <div
                                key={`${item.sourceCollection}-${item.sourceId}-${idx}`}
                                class='position-relative border rounded overflow-hidden'
                                style={`
                                    width: ${compact ? '80px' : '120px'};
                                    height: ${compact ? '80px' : '120px'};
                                    cursor: pointer;
                                    flex-shrink: 0;
                                `}
                                onClick={() => this.handleImageClick(item)}
                            >
                                <img
                                    src={thumb}
                                    alt={item.image.name || item.image.originalFilename || ''}
                                    loading='lazy'
                                    style='width: 100%; height: 100%; object-fit: cover;'
                                />
                                {!compact && (
                                    <div
                                        class='position-absolute bottom-0 start-0 w-100 px-1'
                                        style='background: rgba(0,0,0,0.6); font-size: 0.65em; line-height: 1.3;'
                                    >
                                        <span class='text-white text-truncate d-block'>
                                            {item.sourceCollection}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Loading spinner */}
                {loading && (
                    <div class='text-center py-3'>
                        <div class='spinner-border spinner-border-sm text-primary' role='status'>
                            <span class='visually-hidden'>Loading...</span>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!loading && images.length === 0 && (
                    <div class='text-center py-4 text-body-secondary'>
                        No images found.
                    </div>
                )}

                {/* Load more */}
                {images.length < total && !loading && (
                    <div class='text-center py-3'>
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-primary'
                            onClick={this.handleLoadMore}
                        >
                            Load more ({images.length} / {total})
                        </button>
                    </div>
                )}

                {/* Full size preview (only in standalone mode, not picker mode) */}
                {selectedImage && !onSelect && (
                    <div
                        class='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                        style='background: rgba(0,0,0,0.8); z-index: 999999; cursor: pointer;'
                        onClick={this.handleClosePreview}
                    >
                        <div class='position-relative' style='max-width: 90vw; max-height: 90vh;'>
                            <img
                                src={this.getImageFull(selectedImage.image)}
                                alt={selectedImage.image.name || ''}
                                style='max-width: 90vw; max-height: 80vh; object-fit: contain;'
                            />
                            <div class='text-white text-center mt-2' style='font-size: 0.85em;'>
                                <div>{selectedImage.image.name || selectedImage.image.originalFilename || ''}</div>
                                <div class='text-white-50'>
                                    Source: {selectedImage.sourceCollection} - {selectedImage.sourceTitle}
                                </div>
                            </div>
                            <button
                                type='button'
                                class='btn btn-sm btn-light position-absolute'
                                style='top: -10px; right: -10px;'
                                onClick={this.handleClosePreview}
                            >
                                <i class='fas fa-times' />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default ImageBrowser;
